import { LoadingButton } from '@mui/lab';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getResourceAllocationMemberGridColumns } from '../../common/grid-columns';
import { IResourceAllocationDataMemberGridColumns, ISkillsData } from '../type';
import ResourceAllocationDataTable from './resource-allocation-data-table';
import { GET_MEMBER_PROJECTS, GET_MEMBER_SKILLS } from '../gql';
import { useQuery } from '@apollo/client';
import AvatarImage from '../../common/avatar-image';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AddToolsForm from './forms/add-tools-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../common/utils/root-state';
import DeleteToolsForm from './forms/delete-tools-form';

const SingleResourceAllocations = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);
  const { resourceAllocations } = location.state;
  const [projectMemberProjects, setProjectMemberProjects] =
    useState<IResourceAllocationDataMemberGridColumns[]>();
  const [memberId, setMemberId] = useState<string>();
  const [currentTool, setCurrentTool] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [edit, setEdit] = useState<boolean>(false);
  const [level, setLevel] = useState<string | null>(null);
  const [validatorId, setValidatorId] = useState<string | null>(null);
  const [memberSkillId, setMemberSkillId] = useState<string | null>(null);
  const currentUserId = useSelector(
    (state: RootState) => state.profileData.profileData?.memberId
  );
  const permissions = useSelector(
    (state: RootState) => state.profileData.profileData?.permissions
  );
  const [memberSkills, setMemberSkills] = useState<ISkillsData[]>();
  const [memberSkillsIdPendingApprovals, setMemberSkillsIdPendingApprovals] =
    useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { data: projectMemberData } = useQuery(GET_MEMBER_PROJECTS, {
    variables: { memberId, isProfileView: true }
  });

  const { refetch: refetchMemberSkills, data: memberSkillsData } = useQuery(
    GET_MEMBER_SKILLS,
    {
      variables: { id: memberId }
    }
  );

  const [isAddToolsFormVisible, setAddToolsFormVisible] = useState(false);
  const [isDeleteToolsFormVisible, setDeleteToolsFormVisible] = useState(false);

  useEffect(() => {
    if (projectMemberData) {
      setProjectMemberProjects(projectMemberData.getProjectAllocatedHours);
      setIsLoading(false);
    }
  }, [projectMemberData]);

  useEffect(() => {
    if (resourceAllocations) {
      setMemberId(resourceAllocations.member.id);
    }
  }, [resourceAllocations]);

  useEffect(() => {
    if (memberSkillsData) {
      setMemberSkills(memberSkillsData.getMemberSkills);
    }
  }, [memberSkillsData]);

  useEffect(() => {
    if (memberSkillsData) {
      const pendingApprovals: string[] = memberSkillsData.getMemberSkills
        .filter((skill: { status: string }) => skill.status === 'APPROVED')
        .map((skill: { id: string }) => skill.id);
      setMemberSkillsIdPendingApprovals(pendingApprovals);
    }
  }, [memberSkillsData]);

  const ToolsIcon1: React.FC = () => (
    <img src="/images/ResourceAllocation/ToolsIcon1.svg" alt="Tools Icon" />
  );

  const ToolsIcon2: React.FC = () => (
    <img src="/images/ResourceAllocation/ToolsIcon2.svg" alt="Tools Icon" />
  );

  const updateResourceAllocationDataMemberCallback =
    async (): Promise<void> => {
      // await refetch();
    };

  const updateMemberSkillsCallback = async (): Promise<void> => {
    await refetchMemberSkills();
  };

  return (
    <Box
      data-testid={'single-resource-allocation-container'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        allignItems: 'flex-start',
        padding: '21px 20px',
        height: 'calc(100vh - 75px)',
        margin: '0px 22px 20px 22px',
        borderRadius: '12px',
        gap: '40px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          color: '#46464F',
          alignItems: 'flex-start'
        }}
      >
        <Box sx={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
          <Typography
            sx={{ fontSize: '28px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => navigate(`/resource-allocations`)}
          >
            <ArrowBackIosIcon fontSize="small" />
          </Typography>
          <Typography sx={{ fontSize: '28px', fontWeight: 700 }}>
            {resourceAllocations.member.displayName}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
          {'User Management > Resource Allocation > ' +
            String(resourceAllocations.member.displayName)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          overflowY: 'auto',
          height: 'calc(100vh - 150px)',
          scrollPadding: '0px',
          '&::-webkit-scrollbar': {
            width: '7px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(217, 217, 217, 0.4)',
            borderRadius: '10px'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            padding: '0px 5px 0px 0px',
            minWidth: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              border: '1px solid #CAC4D0',
              borderRadius: '12px',
              padding: '14px 12px'
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '32px',
                  color: '#1A1B1F'
                }}
              >
                Basic Profile Details
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                rowGap: '10px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '11px',
                  alignItems: 'center'
                }}
              >
                <AvatarImage
                  providerId={resourceAllocations.member.userPrincipalName}
                  width={'142px'}
                  height={'142px'}
                  alt={resourceAllocations.member.displayName}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '11px'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '24px',
                      fontWeight: 400,
                      lineHeight: '32px',
                      color: '#1A1B1F'
                    }}
                  >
                    {resourceAllocations.member.displayName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#44474E'
                    }}
                  >
                    {resourceAllocations.member.jobTitle}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '18px',
                  marginX: 'auto'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '18px'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#74777F',
                      minWidth: '136px'
                    }}
                  >
                    Full Name
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: '#1A1B1F'
                    }}
                  >
                    {resourceAllocations.member.displayName}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '18px'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#74777F',
                      minWidth: '136px'
                    }}
                  >
                    Contact No
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: resourceAllocations.member.mobilePhone
                        ? '#1A1B1F'
                        : '#74777F'
                    }}
                  >
                    {resourceAllocations.member.mobilePhone
                      ? resourceAllocations.member.mobilePhone
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '18px'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#74777F',
                      minWidth: '136px'
                    }}
                  >
                    Email
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: '#1A1B1F'
                    }}
                  >
                    {resourceAllocations.member.userPrincipalName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              border: '1px solid #CAC4D0',
              borderRadius: '12px',
              padding: '14px 12px'
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Be Vietnam Pro',
                    fontSize: '20px',
                    fontWeight: 600,
                    lineHeight: '32px',
                    color: '#1A1B1F'
                  }}
                >
                  Tools/Technology Competency
                </Typography>

                <Box>
                  {currentUserId === memberId && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '16px'
                      }}
                    >
                      <LoadingButton
                        type="submit"
                        sx={{
                          padding: '10px 25px 10px 25px',
                          fontFamily: 'Be Vietnam Pro',
                          fontSize: '14px',
                          lineHeight: '20px',
                          textAlign: 'center',
                          fontWeight: 500,
                          color: '#295DAB',
                          backgroundColor: '#C9E6FF',
                          textTransform: 'none',
                          borderRadius: '100px'
                        }}
                        onClick={() => {
                          setAddToolsFormVisible(true);
                          setEdit(false);
                        }}
                      >
                        Add Tools and Technologies
                      </LoadingButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '13px',
                flexWrap: 'wrap'
              }}
            >
              {memberSkills?.length !== 0 ? (
                memberSkills?.map((skills: ISkillsData) => (
                  <Box
                    key={skills.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      backgroundColor:
                        memberSkillsIdPendingApprovals.includes(skills.id) &&
                        hoveredIndex === skills.id
                          ? '#E4E8F5CC'
                          : '#295DAB1F',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      padding: '14px 7px 14px 7px',
                      position: 'relative',
                      minWidth: '80px'
                    }}
                    onMouseEnter={() =>
                      memberSkillsIdPendingApprovals.includes(skills.id) &&
                      setHoveredIndex(skills.id)
                    }
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <Box
                      sx={{
                        maxHeight: '24px',
                        maxWidth: '24px',
                        opacity:
                          !memberSkillsIdPendingApprovals.includes(skills.id) ||
                          (hoveredIndex === skills.id &&
                            (currentUserId === memberId ||
                              permissions?.resource_tool.update))
                            ? 0.3
                            : 1
                      }}
                    >
                      {skills.proficiencyLevel === 'EXPERT' && <ToolsIcon1 />}
                      {skills.proficiencyLevel === 'INTERMEDIATE' && (
                        <ToolsIcon2 />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: 'Be Vietnam Pro',
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.5px',
                          fontWeight: 400,
                          color: '#2F3033',
                          opacity:
                            !memberSkillsIdPendingApprovals.includes(
                              skills.id
                            ) ||
                            (hoveredIndex === skills.id &&
                              (currentUserId === memberId ||
                                permissions?.resource_tool.update))
                              ? 0.3
                              : 1
                        }}
                      >
                        {skills.skill.name}
                      </Typography>
                    </Box>
                    {hoveredIndex === skills.id &&
                      (currentUserId === memberId ||
                        permissions?.resource_tool.update) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <IconButton
                            sx={{
                              '&:hover': {
                                backgroundColor: 'transparent'
                              }
                            }}
                            onClick={() => {
                              setMemberSkillId(skills.id);
                              setDeleteToolsFormVisible(true);
                            }}
                          >
                            <DeleteOutlineIcon
                              sx={{
                                color: '#BA1A1A'
                              }}
                            />
                          </IconButton>
                          <IconButton
                            sx={{
                              '&:hover': {
                                backgroundColor: 'transparent'
                              }
                            }}
                            onClick={() => {
                              setMemberSkillId(skills.id);
                              setCurrentTool(skills.skill);
                              setLevel(skills.proficiencyLevel);
                              setValidatorId(skills.validatorId ?? '');
                              setEdit(true);
                              setAddToolsFormVisible(true);
                            }}
                          >
                            <EditOutlinedIcon
                              sx={{
                                color: '#295DAB'
                              }}
                            />
                          </IconButton>
                        </Box>
                      )}
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    padding: '5px 7px 5px 7px',
                    backgroundColor: '#d9dff1 '
                  }}
                >
                  <Typography>No Tools and Technologies are added</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box>
            <ResourceAllocationDataTable
              columns={getResourceAllocationMemberGridColumns()}
              isLoading={isLoading}
              data={projectMemberProjects ?? []}
              updateResourceAllocationDataMemberCallback={
                updateResourceAllocationDataMemberCallback
              }
            />
          </Box>
        </Box>
      </Box>
      {isAddToolsFormVisible && (
        <AddToolsForm
          edit={edit}
          currentTool={currentTool ?? undefined}
          currentLevel={level ?? undefined}
          memberSkillId={memberSkillId ?? undefined}
          validatorId={validatorId ?? undefined}
          handleClose={() => setAddToolsFormVisible(false)}
          updateMemberSkillsCallback={updateMemberSkillsCallback}
          memberSkills={memberSkills}
          memberId={resourceAllocations.member.id}
        />
      )}

      <DeleteToolsForm
        isVisible={isDeleteToolsFormVisible}
        onCancel={() => setDeleteToolsFormVisible(false)}
        onSuccess={() => setDeleteToolsFormVisible(false)}
        memberSkillId={memberSkillId ?? undefined}
        updateMemberSkillsCallback={updateMemberSkillsCallback}
      />
    </Box>
  );
};

export default SingleResourceAllocations;
